import { DateTime } from 'luxon'

const INSTALLMENT_TZ = 'America/New_York'
export const FIRST_PAYMENT_MAX_DAYS = 14

function todayEt () {
  return DateTime.now().setZone(INSTALLMENT_TZ).startOf('day')
}

export function firstPaymentMinIsoDate () {
  return todayEt().plus({ days: 1 }).toISODate()
}

export function firstPaymentMaxIsoDate () {
  return todayEt().plus({ days: FIRST_PAYMENT_MAX_DAYS }).toISODate()
}

export function isFirstPaymentDateAllowed (isoDate) {
  const day = String(isoDate || '').slice(0, 10)
  if (!day) return false
  return day >= firstPaymentMinIsoDate() && day <= firstPaymentMaxIsoDate()
}

export function clampFirstPaymentDate (isoDate) {
  const min = firstPaymentMinIsoDate()
  const max = firstPaymentMaxIsoDate()
  const day = String(isoDate || '').slice(0, 10)
  if (!day || day < min) return min
  if (day > max) return max
  return day
}

export function formatUsd (value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0))
}

/** `q22020` → `Q2 2020`. Leaves already-formatted labels alone. */
export function formatQuarterLabel (quarter) {
  const raw = String(quarter || '').trim()
  if (!raw) return ''
  const compact = raw.match(/^q([1-4])(\d{4})$/i)
  if (compact) return `Q${compact[1]} ${compact[2]}`
  return raw
}

export function formatPlanDate (iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-')
  return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export function tomorrowIsoDate () {
  return firstPaymentMinIsoDate()
}

export function splitAmounts (total, count) {
  const cents = Math.round(Number(total) * 100)
  const n = Number(count)
  const base = Math.floor(cents / n)
  const remainder = cents - base * n
  return Array.from({ length: n }, (_, i) => ((base + (i === n - 1 ? remainder : 0)) / 100))
}

export function addWeeks (iso, weeks) {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  date.setUTCDate(date.getUTCDate() + (weeks * 7))
  return date.toISOString().slice(0, 10)
}

export function addMonths (iso, months) {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1 + months, d))
  return date.toISOString().slice(0, 10)
}

export function previewSchedule (startDate, frequency, count, total) {
  const amounts = splitAmounts(total, count)
  return amounts.map((amount, index) => ({
    sequence: index + 1,
    amount,
    scheduledDate: frequency === 'weekly' ? addWeeks(startDate, index) : addMonths(startDate, index)
  }))
}

export function buildPaymentHistoryRows (schedule = [], charges = []) {
  const bySeq = new Map((charges || []).map((c) => [Number(c.sequence), c]))
  const rows = (schedule || []).map((s) => {
    const charge = bySeq.get(Number(s.sequence))
    bySeq.delete(Number(s.sequence))
    return {
      key: `seq-${s.sequence}`,
      sequence: s.sequence,
      amount: charge?.amount ?? s.amount,
      date: charge?.scheduledDate || s.scheduledDate,
      attemptedAt: charge?.attemptedAt || null,
      status: charge?.status || 'upcoming',
      responseText: charge?.responseText || ''
    }
  })
  for (const charge of [...bySeq.values()].sort((a, b) => Number(a.sequence) - Number(b.sequence))) {
    rows.push({
      key: charge.chargeId || `extra-${charge.sequence}`,
      sequence: charge.sequence,
      amount: charge.amount,
      date: charge.scheduledDate,
      attemptedAt: charge.attemptedAt || null,
      status: charge.status,
      responseText: charge.responseText || ''
    })
  }
  return rows
}
